import type {
	SortState,
	ResizedColumn,
	ColumnVisibility,
} from '~~/layers/primitives/shared/types/dataTable';

const PREFERENCES_COOKIE_KEY = 'lcp-user-preferences';
const STATE_KEY = 'lcp-user-preferences-state';

export interface DataTablePreferences {
	sortState?: SortState[];
	pageSize?: number;
	columnWidths?: ResizedColumn[];
	groupByFields?: string[];
	columnOrder?: string[];
	columnVisibility?: ColumnVisibility[];
}

export interface SidebarItemPreference {
	order?: number;
	visible?: boolean;
}

export interface SidebarPreferences {
	items: Record<string, SidebarItemPreference>;
}

/**
 * All user preferences stored in the cookie
 */
export interface JobPreferences {
	createFormType?: 'guided' | 'standard';
	enableSearching?: boolean;
	enableFiltering?: boolean;
	enableGrouping?: boolean;
}

export interface UserPreferences {
	dataTables: Record<string, DataTablePreferences>;
	sidebar: SidebarPreferences;
	jobs: JobPreferences;
}

const DEFAULT_PREFERENCES: UserPreferences = {
	dataTables: {},
	sidebar: { items: {} },
	jobs: {},
};

/**
 * Composable for managing user preferences with cookie persistence and global state.
 *
 * Uses `useCookieStorage` for SSR-compatible persistence and `useState` for
 * global reactivity across components.
 *
 * @example
 * ```vue
 * <script setup>
 * const { getDataTablePreferences, setDataTableSort, setDataTablePageSize } = useUserPreferences();
 * const prefs = getDataTablePreferences('jobs-table');
 * </script>
 *
 * <template>
 *   <UiDataTable
 *     :default-sort="prefs.sortState"
 *     :items-per-page="prefs.pageSize ?? 25"
 *     @sort-change="(sorts) => setDataTableSort('jobs-table', sorts)"
 *     @page-size-change="(size) => setDataTablePageSize('jobs-table', size)"
 *   />
 * </template>
 * ```
 */
export function useUserPreferences() {
	const cookie = useCookieStorage<UserPreferences>(PREFERENCES_COOKIE_KEY, {
		default: () => ({ ...DEFAULT_PREFERENCES }),
	});

	const state = useState<UserPreferences>(STATE_KEY, () => {
		return cookie.value ?? { ...DEFAULT_PREFERENCES };
	});

	// In statically-generated builds, useState hydrates from the build-time payload
	// which contains defaults (no user cookies exist at build time). On the client,
	// useCookie reads from document.cookie and has the actual user preferences.
	// Re-sync state from the cookie to pick them up.
	if (import.meta.client) {
		state.value = cookie.value ?? { ...DEFAULT_PREFERENCES };
	}

	watch(
		state,
		(newValue) => {
			cookie.value = newValue;
		},
		{ deep: true },
	);

	if (!state.value.dataTables || !state.value.sidebar || !state.value.jobs) {
		state.value = { ...DEFAULT_PREFERENCES, ...state.value };
	}

	const getDataTablePreferences = (tableKey: string): DataTablePreferences => {
		return state.value.dataTables[tableKey] ?? {};
	};

	const setDataTableSort = (tableKey: string, sortState: SortState[]) => {
		if (!state.value.dataTables[tableKey]) {
			state.value.dataTables[tableKey] = {};
		}
		state.value.dataTables[tableKey].sortState = sortState;
	};

	const setDataTablePageSize = (tableKey: string, pageSize: number) => {
		if (!state.value.dataTables[tableKey]) {
			state.value.dataTables[tableKey] = {};
		}
		state.value.dataTables[tableKey].pageSize = pageSize;
	};

	const setDataTableColumnWidths = (tableKey: string, columnWidths: ResizedColumn[]) => {
		if (!state.value.dataTables[tableKey]) {
			state.value.dataTables[tableKey] = {};
		}
		state.value.dataTables[tableKey].columnWidths = columnWidths;
	};

	const setDataTableGroupByFields = (tableKey: string, groupByFields: string[]) => {
		if (!state.value.dataTables[tableKey]) {
			state.value.dataTables[tableKey] = {};
		}
		state.value.dataTables[tableKey].groupByFields = groupByFields;
	};

	const setDataTableColumnOrder = (tableKey: string, columnOrder: string[]) => {
		if (!state.value.dataTables[tableKey]) {
			state.value.dataTables[tableKey] = {};
		}
		state.value.dataTables[tableKey].columnOrder = columnOrder;
	};

	const setDataTableColumnVisibility = (
		tableKey: string,
		columnVisibility: ColumnVisibility[],
	) => {
		if (!state.value.dataTables[tableKey]) {
			state.value.dataTables[tableKey] = {};
		}
		state.value.dataTables[tableKey].columnVisibility = columnVisibility;
	};

	const clearDataTablePreferences = (tableKey: string) => {
		delete state.value.dataTables[tableKey];
	};

	const getSidebarPreferences = (): SidebarPreferences => {
		return state.value.sidebar ?? { items: {} };
	};

	const getSidebarItemPreference = (pageId: string): SidebarItemPreference => {
		return state.value.sidebar?.items[pageId] ?? {};
	};

	const setSidebarItemOrder = (pageId: string, order: number) => {
		if (!state.value.sidebar) {
			state.value.sidebar = { items: {} };
		}
		if (!state.value.sidebar.items[pageId]) {
			state.value.sidebar.items[pageId] = {};
		}
		state.value.sidebar.items[pageId].order = order;
	};

	const setSidebarItemVisibility = (pageId: string, visible: boolean) => {
		if (!state.value.sidebar) {
			state.value.sidebar = { items: {} };
		}
		if (!state.value.sidebar.items[pageId]) {
			state.value.sidebar.items[pageId] = {};
		}
		state.value.sidebar.items[pageId].visible = visible;
	};

	const setSidebarItemPreference = (pageId: string, prefs: SidebarItemPreference) => {
		if (!state.value.sidebar) {
			state.value.sidebar = { items: {} };
		}
		state.value.sidebar.items[pageId] = prefs;
	};

	const clearSidebarItemPreference = (pageId: string) => {
		if (state.value.sidebar?.items[pageId]) {
			delete state.value.sidebar.items[pageId];
		}
	};

	const clearSidebarPreferences = () => {
		state.value.sidebar = { items: {} };
	};

	const getJobCreateFormType = (): 'guided' | 'standard' | undefined => {
		return state.value.jobs?.createFormType;
	};

	const setJobCreateFormType = (formType: 'guided' | 'standard') => {
		if (!state.value.jobs) {
			state.value.jobs = {};
		}
		state.value.jobs.createFormType = formType;
	};

	const getJobGridDisplayPrefs = () => {
		return {
			enableSearching: state.value.jobs?.enableSearching,
			enableFiltering: state.value.jobs?.enableFiltering,
			enableGrouping: state.value.jobs?.enableGrouping,
		};
	};

	const setJobGridDisplayPref = (
		key: 'enableSearching' | 'enableFiltering' | 'enableGrouping',
		value: boolean,
	) => {
		if (!state.value.jobs) {
			state.value.jobs = {};
		}
		state.value.jobs[key] = value;
	};

	const clearAllPreferences = () => {
		state.value = { ...DEFAULT_PREFERENCES };
	};

	return {
		preferences: readonly(state),

		getDataTablePreferences,
		setDataTableSort,
		setDataTablePageSize,
		setDataTableColumnWidths,
		setDataTableGroupByFields,
		setDataTableColumnOrder,
		setDataTableColumnVisibility,
		clearDataTablePreferences,

		getSidebarPreferences,
		getSidebarItemPreference,
		setSidebarItemOrder,
		setSidebarItemVisibility,
		setSidebarItemPreference,
		clearSidebarItemPreference,
		clearSidebarPreferences,

		getJobCreateFormType,
		setJobCreateFormType,
		getJobGridDisplayPrefs,
		setJobGridDisplayPref,

		clearAllPreferences,
	};
}
